/**
 * Single source of truth for the `?netobs=1` master toggle. Cached on
 * first call so renderers (which run at 60Hz) don't re-parse the URL
 * every frame. URL is immutable for the session, so first-call cache
 * is correct.
 *
 * Gating — the diagnostic surface, NOT the dev-build env:
 *  - `/debug` route → on (the canonical diagnostic surface)
 *  - `?netobs=1` → on
 *  - `?netobsTrace=1` → trace on without auto-opening diagnostic windows
 *  - `?debug=1` → on (back-compat with existing diagnostic surfaces)
 *  - `window.playwrightTest === true` → on (E2E)
 *  - else off
 *
 * 2026-05-29: deliberately NOT gated on `window.forceDebug`. That flag is
 * set across the whole development/PTR tier, so keying off it lit up every
 * netobs overlay on /pve, /brawl, etc. (DJ: "if I pick pve it shouldn't load
 * all our stutter detect"). Diagnostics are now scoped to the /debug surface.
 */
import { bootPathname, bootSearchParams } from '../../../config/boot/bootUrl';

let cached: boolean | null = null;

export function isNetobsEnabled(): boolean {
  if (cached !== null) return cached;
  if (typeof window === 'undefined') {
    cached = false;
    return false;
  }
  if ((window as any).playwrightTest) { cached = true; return true; }
  try {
    if (bootPathname().startsWith('/debug')) { cached = true; return true; }
    const params = bootSearchParams();
    if (params.get('netobs') === '1') { cached = true; return true; }
    if (params.get('netobsTrace') === '1') { cached = true; return true; }
    if (params.get('debug') === '1') { cached = true; return true; }
  } catch { /* ignore */ }
  cached = false;
  return false;
}

/** Test/dev override: reset the cache so the next call re-parses. */
export function resetNetobsEnabledCache(): void {
  cached = null;
}
